import {createSlice} from '@reduxjs/toolkit';
import {initialState} from './initialState';
import {
  maskDateOfBirthOnType,
  validateAndUpdateApartment,
  validateAndUpdateCity,
  validateAndUpdateDateOfBirth,
  validateAndUpdateDay,
  validateAndUpdateDealerSearchString,
  validateAndUpdateEmail,
  validateAndUpdateFirstName,
  validateAndUpdateIncome,
  validateAndUpdateLastName,
  validateAndUpdateMonth,
  validateAndUpdatePhoneNumber,
  validateAndUpdatePostalCode,
  validateAndUpdateStateOrProvince,
  validateAndUpdateStreetAddress,
  validateAndUpdateYear,
  maskPostalCodeOnType
} from '../validation/InputValidation';
import {assignOrderToRoute} from '../utils/functions/Functions';

export const customerDataSlice = createSlice({
  name: 'customerDataSlice',
  initialState,
  reducers: {
    updateAddressData: (state, action) => {
      state.streetAddress.value = action.payload.streetAddress ? action.payload.streetAddress : '';
      validateAndUpdateStreetAddress(state);
      state.apartment.value = action.payload.apartment ? action.payload.apartment : '';
      validateAndUpdateApartment(state);
      state.city.value = action.payload.city ? action.payload.city : '';
      validateAndUpdateCity(state);
      state.stateOrProvince.value = action.payload.state ? action.payload.state : '';
      validateAndUpdateStateOrProvince(state);
      state.postalCode.value = action.payload.postalCode ? action.payload.postalCode : '';
      validateAndUpdatePostalCode(state);
    },
    updatePIFirstName: (state, action) => {
      state.firstName.value = action.payload;
      validateAndUpdateFirstName(state);
    },
    updatePILastName: (state, action) => {
      state.lastName.value = action.payload;
      validateAndUpdateLastName(state);
    },
    updatePIDOBMonth: (state, action) => {
      state.dateOfBirth.month.value = action.payload;
      if (state.dateOfBirth.day.value && state.dateOfBirth.year.value) {
        validateAndUpdateMonth(state);
        validateAndUpdateDay(state);
        validateAndUpdateYear(state);
      }
    },
    updatePIDOBDay: (state, action) => {
      state.dateOfBirth.day.value = action.payload;
      if (state.dateOfBirth.month.value && state.dateOfBirth.year.value) {
        validateAndUpdateMonth(state);
        validateAndUpdateDay(state);
        validateAndUpdateYear(state);
      }
    },
    updatePIDOBYear: (state, action) => {
      state.dateOfBirth.year.value = action.payload;
      if (state.dateOfBirth.month.value && state.dateOfBirth.day.value) {
        validateAndUpdateMonth(state);
        validateAndUpdateDay(state);
        validateAndUpdateYear(state);
      }
    },
    updateDateOfBirth: (state, action) => {
      state.dateOfBirth.value = maskDateOfBirthOnType(action.payload);
      validateAndUpdateDateOfBirth(state);
    },
    updateStreetAddress: (state, action) => {
      state.streetAddress.value = action.payload;
      validateAndUpdateStreetAddress(state);
    },
    updateApartment: (state, action) => {
      state.apartment.value = action.payload;
      validateAndUpdateApartment(state);
    },
    updateCity: (state, action) => {
      state.city.value = action.payload;
      validateAndUpdateCity(state);
    },
    updateStateOrProvince: (state, action) => {
      state.stateOrProvince.value = action.payload;
      validateAndUpdateStateOrProvince(state);
    },
    updatePostalCode: (state, action) => {
      state.postalCode.value = state.countryCode.value === 'CAN' ? maskPostalCodeOnType(action.payload) : action.payload;
      validateAndUpdatePostalCode(state);
    },
    updateIncome: (state, action) => {
      state.income.value = action.payload;
      validateAndUpdateIncome(state);
    },
    updatePersonalInfoEditMode: (state, action) => {
      state.cardEditFlags.personalInfoEditMode.value = action.payload;
    },
    updateIncomeEditMode: (state, action) => {
      state.cardEditFlags.incomeEditMode.value = action.payload;
    },
    updateResidenceEditMode: (state, action) => {
      state.cardEditFlags.residenceEditMode.value = action.payload;
    },
    updateVehicleEditMode: (state, action) => {
      state.cardEditFlags.vehicleEditMode.value = action.payload;
    },
    updateEmail: (state, action) => {
      state.email.value = action.payload;
      validateAndUpdateEmail(state);
    },
    updateSoftInquiryConsent: (state, action) => {
      state.softInquiryConsent.value = action.payload;
    },
    updateAcceptedTermsAndConditions: (state, action) => {
      state.acceptedTermsAndConditions.value = action.payload;
    },
    updateFordHeaderFooterLoading: (state, action) => {
      state.fordHeaderFooterLoading.value = action.payload;
    },
    updateHighestPageHit: (state, action) => {
      const currentIndex = typeof action.payload === 'number' ? action.payload : assignOrderToRoute(window.location.pathname);
      if (currentIndex > state.highestPageHit.value) {
        state.highestPageHit.value = currentIndex;
      }
    },
    resetHighestPageHit: (state) => {
      state.highestPageHit.value = 1;
    },
    updateDecisionCode: (state, action) => {
      state.decisionCode.value = action.payload;
    },
    updateConversationId: (state, action) => {
      state.conversationId.value = action.payload;
    },
    updateRate: (state, action) => {
      state.rate.value = action.payload;
    },
    updateTotalApprovedAmount: (state, action) => {
      state.totalApprovedAmount.value = action.payload;
    },
    updateExpirationDate: (state, action) => {
      state.expirationDate.value = action.payload;
    },
    updateToggleDealerSubmissionDialog: (state, action) => {
      state.toggleDealerSubmissionDialog.value = action.payload;
    },
    updatePhoneNumber: (state, action) => {
      state.phoneNumber.value = action.payload;
      validateAndUpdatePhoneNumber(state);
    },
    updateShowPreQualificationAmount: (state, action) => {
      state.showPreQualificationAmount.value = action.payload;
    },
    updatePreferredContactMethod: (state, action) => {
      state.preferredContactMethod.value = action.payload;
      validateAndUpdateEmail(state);
      validateAndUpdatePhoneNumber(state);
    },
    updateDealerNotes: (state, action) => {
      state.dealerNotes.value = action.payload;
    },
    updateToggleDealerConnectDialog: (state, action) => {
      state.toggleDealerConnectDialog.value = action.payload;
    },
    updateSelectedDealer: (state, action) => {
      state.selectedDealer.value = action.payload;
    },
    updateToggleDealerConfirmationDialog: (state, action) => {
      state.toggleDealerConfirmationPage.value = action.payload;
    },
    updateDealerSearchString: (state, action) => {
      const origValue = state.dealerSearchString.value;
      state.dealerSearchString.value = action.payload;
      validateAndUpdateDealerSearchString(state);
      if (state.dealerSearchString.hasError) {
        state.dealerSearchString.value = origValue;
      }
    },
    updateDealerSearchType: (state, action) => {
      state.dealerSearchType.value = action.payload;
    },
    updateDealerLeadSubmitted: (state, action) => {
      state.dealerLeadSubmitted.value = action.payload;
    },
    updateShowCancelPreQualModal: (state, action) => {
      state.showCancelPreQualModal.value = action.payload;
    },
    updateDealerLeadStatusMessage: (state, action) => {
      state.dealerLeadStatusMessage.value = action.payload;
    },
    updateIsSelected: (state, action) => {
      state.isSelected.value = action.payload;
    },
    updateShowModalFlag: (state, action) => {
      state.showModalFlag.value = action.payload;
    },
    updateCurrentModalPage: (state, action) => {
      state.currentModalPage.value = action.payload;
    },
    updateCurrentModalPageWithState: (state, action) => {
      state.currentModalPage = action.payload;
    },
    updateIsDealerHostApplication: (state, action) => {
      state.isDealerHostApplication.value = action.payload;
    },

    updateTier3PACode: (state, action) => {
      state.tier3PACode.value = action.payload;
    },

    updateTier3Vin: (state, action) => {
      state.tier3Vin.value = action.payload;
    },
    updateResult: (state, action) => {
      state.result.value = action.payload;
    },
    updateIndexSource: (state, action) => {
      state.indexSource.value = action.payload;
    },
    updatePrequalSource: (state, action) => {
      state.prequalSource.value = action.payload;
    },
    updateSpecialEvent: (state, action) => {
      state.specialEvent.value = Boolean(action.payload);
    },
    updateSelectedVehicleYear: (state, action) => {
      state.selectedVehicle.year.value = action.payload;
    },
    updateSelectedVehicleModel: (state, action) => {
      state.selectedVehicle.model.value = action.payload;
    },
    updateSelectedVehicleModelDisplayName: (state, action) => {
      state.selectedVehicle.model.displayName = action.payload;
    },
    updateSelectedVehicleMake: (state, action) => {
      state.selectedVehicle.make.value = action.payload;
    },
    updateSelectedVehicleTrim: (state, action) => {
      state.selectedVehicle.trim.value = action.payload;
    },
    updateSelectedVehicleValue: (state, action) => {
      state.selectedVehicle.value = action.payload;
    },
    updateSkipAddressStandardization: (state, action) => {
      state.skipAddressStandardization.value = action.payload;
    },
    updateLoadingPageTagged: (state, action) => {
      state.loadingPageTagged = action.payload;
    },
    updateCountryCode: (state, action) => {
      state.countryCode.value = action.payload;
    },
    updateConfig: (state, action) => {
      state.config = action.payload;
    },
    validateFirstName(state) {
      validateAndUpdateFirstName(state);
    },
    validateCustomerData: (state) => {
      validateAndUpdateFirstName(state);
      validateAndUpdateLastName(state);
      validateAndUpdateEmail(state);
      validateAndUpdateDateOfBirth(state);
      validateAndUpdateStreetAddress(state);
      validateAndUpdateApartment(state);
      validateAndUpdateCity(state);
      validateAndUpdateStateOrProvince(state);
      validateAndUpdatePostalCode(state);
      validateAndUpdateIncome(state);
    },
    resetCustomerData: (state) => {
      state.firstName.value = '';
      state.firstName.hasError = true;
      state.firstName.errorMessage = '';
      state.lastName.value = '';
      state.lastName.hasError = true;
      state.lastName.errorMessage = '';
      state.dateOfBirth.value = '';
      state.dateOfBirth.month.value = '';
      state.dateOfBirth.month.hasError = true;
      state.dateOfBirth.day.value = '';
      state.dateOfBirth.day.hasError = true;
      state.dateOfBirth.year.value = '';
      state.dateOfBirth.year.hasError = true;
      state.dateOfBirth.errorMessage.value = '';
      state.dateOfBirth.errorMessage.hasError = true;
      state.streetAddress.value = '';
      state.streetAddress.hasError = true;
      state.streetAddress.errorMessage = '';
      state.apartment.value = '';
      state.apartment.hasError = true;
      state.apartment.errorMessage = '';
      state.city.value = '';
      state.city.hasError = true;
      state.city.errorMessage = '';
      state.stateOrProvince.value = '';
      state.stateOrProvince.hasError = true;
      state.stateOrProvince.errorMessage = '';
      state.postalCode.value = '';
      state.postalCode.hasError = true;
      state.postalCode.errorMessage = '';
      state.income.value = '';
      state.income.hasError = true;
      state.income.errorMessage = '';
      state.cardEditFlags.personalInfoEditMode.value = false;
      state.cardEditFlags.incomeEditMode.value = false;
      state.cardEditFlags.residenceEditMode.value = false;
      state.cardEditFlags.vehicleEditMode.value = false;
      state.email.value = '';
      state.email.hasError = true;
      state.email.errorMessage = '';
      state.softInquiryConsent.value = false;
      state.acceptedTermsAndConditions.value = false;
      state.fordHeaderFooterLoading.value = false;
      state.decisionCode.value = '';
      state.conversationId.value = '';
      state.rate.value = '';
      state.totalApprovedAmount.value = 0;
      state.expirationDate.value = '';
      state.phoneNumber.value = '';
      state.phoneNumber.hasError = false;
      state.phoneNumber.errorMessage = '';
      state.toggleDealerSubmissionDialog.value = false;
      state.toggleDealerConnectDialog.value = false;
      state.dealerLeadSubmitted.value = false;
      state.showPreQualificationAmount.value = true;
      state.preferredContactMethod.value = 'Email';
      state.dealerNotes.value = '';
      state.selectedDealer.value = {};
      state.toggleDealerConfirmationPage.value = false;
      state.DealerConfirmationWithoutMapFeatureFlag.value = false;
      state.dealerSearchString.value = '';
      state.dealerSearchType.value = '';
      state.showCancelPreQualModal.value = false;
      state.highestPageHit.value = 0;
      state.dealerLeadStatusMessage.value = '';
      state.currentModalPage.value = '';
      state.isDealerHostApplication.value = false;
      state.specialEvent.value = false;
      state.selectedVehicle.year.value = '';
      state.selectedVehicle.model.value = '';
      state.selectedVehicle.model.displayName = '';
      state.selectedVehicle.trim.value = '';
      state.selectedVehicle.value = false;
      state.result.value = {};
      state.skipAddressStandardization.value = false;
      state.tier3Vin.value = '';
      state.tier3PACode.value = '';
    },
    resetVehicleInfo: (state) => {
      state.selectedVehicle.year.value = '';
      state.selectedVehicle.model.value = '';
      state.selectedVehicle.trim.value = '';
    },
    updateOptimizelyExperimentData: (state, action) => {
      state.optimizelyExperimentData = action.payload;
    },
    updateModalUpdateFlag: (state, action) => {
      state.modalUpdateFlag = action.payload;
    },
    updateLanguageCode: (state, action) => {
      state.languageCode = action.payload;
    },
    updateRecaptchaOptimizelyData: (state, action) => {
      state.recaptchaOptimizelyData = action.payload;
    }
  }
});

export const {
  updateAddressData,
  updatePIFirstName,
  updatePILastName,
  updatePIDOBMonth,
  updatePIDOBDay,
  updatePIDOBYear,
  updateDateOfBirth,
  updateStreetAddress,
  updateCity,
  updateStateOrProvince,
  updatePostalCode,
  updateIncome,
  updatePersonalInfoEditMode,
  updateIncomeEditMode,
  updateResidenceEditMode,
  updateEmail,
  updateSoftInquiryConsent,
  updateAcceptedTermsAndConditions,
  updateFordHeaderFooterLoading,
  updateHighestPageHit,
  updateApartment,
  updateDecisionCode,
  updateConversationId,
  updateRate,
  updateTotalApprovedAmount,
  updateExpirationDate,
  updateToggleDealerSubmissionDialog,
  updateShowPreQualificationAmount,
  updatePhoneNumber,
  updatePreferredContactMethod,
  updateDealerNotes,
  updateToggleDealerConnectDialog,
  updateSelectedDealer,
  updateToggleDealerConfirmationDialog,
  updateDealerSearchString,
  updateDealerSearchType,
  updateDealerLeadSubmitted,
  updateShowCancelPreQualModal,
  updateDealerLeadStatusMessage,
  resetCustomerData,
  updateIsSelected,
  resetHighestPageHit,
  updateShowModalFlag,
  updateCurrentModalPage,
  updateCurrentModalPageWithState,
  updateIsDealerHostApplication,
  updateTier3PACode,
  updateTier3Vin,
  updateResult,
  updateIndexSource,
  updatePrequalSource,
  updateSelectedVehicleYear,
  updateSelectedVehicleModel,
  updateSelectedVehicleModelDisplayName,
  updateSelectedVehicleTrim,
  updateSelectedVehicleValue,
  updateSelectedVehicleMake,
  updateSpecialEvent,
  updateVehicleEditMode,
  updateSkipAddressStandardization,
  validateCustomerData,
  resetVehicleInfo,
  updateOptimizelyExperimentData,
  updateLoadingPageTagged,
  updateCountryCode,
  updateModalUpdateFlag,
  updateLanguageCode,
  updateConfig,
  updateRecaptchaOptimizelyData
} = customerDataSlice.actions;

export default customerDataSlice.reducer;
